import React, { useEffect, useMemo, useState } from 'react';
import { Popover } from 'antd';
import moment from 'moment';
import { fetchDailyTaskCountByDateRange } from '../../fetch/fetch';

interface DateNavigationHeaderProps {
  onDateChange: (date: string) => void;
  disabled?: boolean;
  checkCanChangeDate?: (currentDate: string) => Promise<boolean>;
  hasTasks?: () => boolean;
  jwtToken?: string;
  totalTaskHours?: number;
  userInitials?: string;
}

const MOBILE_BREAKPOINT = 768;

const LoggedIcon16: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_170_1048)">
      <path
        d="M8 0C6.41775 0 4.87103 0.469192 3.55544 1.34824C2.23985 2.22729 1.21447 3.47672 0.608967 4.93853C0.00346628 6.40034 -0.15496 8.00887 0.153721 9.56072C0.462403 11.1126 1.22433 12.538 2.34315 13.6569C3.46197 14.7757 4.88743 15.5376 6.43928 15.8463C7.99113 16.155 9.59966 15.9965 11.0615 15.391C12.5233 14.7855 13.7727 13.7602 14.6518 12.4446C15.5308 11.129 16 9.58225 16 8C15.9978 5.87895 15.1542 3.84542 13.6544 2.34562C12.1546 0.845814 10.121 0.00223986 8 0ZM11.5123 6.58923L7.20462 10.8969C7.14746 10.9541 7.07959 10.9995 7.00489 11.0305C6.93018 11.0615 6.8501 11.0774 6.76923 11.0774C6.68836 11.0774 6.60828 11.0615 6.53358 11.0305C6.45887 10.9995 6.391 10.9541 6.33385 10.8969L4.48769 9.05077C4.37222 8.9353 4.30735 8.77868 4.30735 8.61538C4.30735 8.45208 4.37222 8.29547 4.48769 8.18C4.60317 8.06453 4.75978 7.99966 4.92308 7.99966C5.08638 7.99966 5.24299 8.06453 5.35846 8.18L6.76923 9.59154L10.6415 5.71846C10.6987 5.66128 10.7666 5.61593 10.8413 5.58499C10.916 5.55404 10.9961 5.53812 11.0769 5.53812C11.1578 5.53812 11.2378 5.55404 11.3126 5.58499C11.3873 5.61593 11.4551 5.66128 11.5123 5.71846C11.5695 5.77564 11.6148 5.84351 11.6458 5.91822C11.6767 5.99292 11.6927 6.07299 11.6927 6.15384C11.6927 6.2347 11.6767 6.31477 11.6458 6.38947C11.6148 6.46418 11.5695 6.53205 11.5123 6.58923Z"
        fill="#0E1F27"
      />
    </g>
    <defs>
      <clipPath id="clip0_170_1048">
        <rect width="16" height="16" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const WeekStrip: React.FC<{
  days: moment.Moment[];
  selected: moment.Moment;
  disabled?: boolean;
  variant?: 'pc' | 'mobile';
  taskHoursByDate?: Record<string, number>;
  onSelectDay: (day: moment.Moment) => void;
  stopOpenPropagation?: boolean;
}> = ({ days, selected, disabled, variant = 'mobile', taskHoursByDate, onSelectDay, stopOpenPropagation }) => {
  return (
    <div style={{ display: 'flex', gap: variant === 'pc' ? 8 : 10, justifyContent: 'center' }}>
      {days.map((day) => {
        const isSelected = day.isSame(selected, 'day');
        const today = moment().startOf('day');
        const dayStart = day.clone().startOf('day');
        const minSelectableDay = today.clone().subtract(2, 'day');
        const isToday = dayStart.isSame(today, 'day');
        const isInSelectableRange =
          dayStart.isSameOrAfter(minSelectableDay, 'day') && dayStart.isSameOrBefore(today, 'day');
        const isMuted = !isInSelectableRange;
        const isDateDisabled = !!disabled || isMuted;

        const size = variant === 'pc' ? 40 : 40;

        const borderRadius = 20;

        const selectedStyle = {
          border: '1px solid #FFA500',
          background: 'rgba(255, 165, 0, 0.20)',
          color: '#0E1F27',
        };

        const todayStyle = {
          border: '1px solid transparent',
          background: '#0E1F27',
          color: '#ffffff',
        };

        const defaultSelectableStyle = {
          border: '1px solid #DFDFDB',
          background: 'transparent',
          color: '#0E1F27',
        };

        const mutedStyle = {
          border: '1px solid #DFDFDB',
          background: 'rgba(223, 223, 219, 0.50)',
          color: '#8C8C86',
        };

        const base = (() => {
          if (isMuted) return mutedStyle;
          if (isSelected) return selectedStyle;
          if (isToday) return todayStyle;
          return defaultSelectableStyle;
        })();

        const dateKey = day.format('YYYY-MM-DD');
        const hours = taskHoursByDate?.[dateKey] ?? 0;
        const hasLogged = hours > 0;

        return (
          <button
            key={day.format('YYYY-MM-DD')}
            type="button"
            disabled={isDateDisabled}
            onClick={(e) => {
              if (isDateDisabled) return;
              if (stopOpenPropagation) e.stopPropagation();
              onSelectDay(day);
            }}
            style={{
              position: 'relative',
              width: size,
              height: size,
              borderRadius,
              border: base.border,
              background: base.background,
              color: base.color,
              fontSize: 12,
              lineHeight: `${size}px`,
              cursor: isDateDisabled ? 'not-allowed' : 'pointer',
              padding: 0,
              fontWeight: isSelected || isToday ? 600 : 400,
            }}
            aria-label={`Select ${day.format('YYYY-MM-DD')}`}
          >
            {day.date()}
            {hasLogged && (
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  width: 16,
                  height: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <LoggedIcon16 />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

const IconClock21: React.FC = () => (
  <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M18.1936 7.12754C18.1936 6.89268 18.2632 6.66309 18.3937 6.46781C18.5242 6.27253 18.7097 6.12033 18.9266 6.03045C19.1436 5.94058 19.3824 5.91706 19.6127 5.96288C19.8431 6.0087 20.0547 6.12179 20.2207 6.28787C20.3868 6.45394 20.4999 6.66552 20.5457 6.89587C20.5915 7.12622 20.568 7.36498 20.4781 7.58196C20.3883 7.79895 20.2361 7.98441 20.0408 8.11489C19.8455 8.24537 19.6159 8.31501 19.3811 8.31501C19.0661 8.31501 18.7641 8.1899 18.5414 7.96721C18.3187 7.74451 18.1936 7.44247 18.1936 7.12754ZM17.0061 4.75259C17.241 4.75259 17.4706 4.68294 17.6659 4.55246C17.8611 4.42198 18.0133 4.23652 18.1032 4.01954C18.1931 3.80256 18.2166 3.5638 18.1708 3.33345C18.125 3.1031 18.0119 2.89151 17.8458 2.72544C17.6797 2.55937 17.4681 2.44627 17.2378 2.40046C17.0075 2.35464 16.7687 2.37815 16.5517 2.46803C16.3347 2.55791 16.1493 2.71011 16.0188 2.90539C15.8883 3.10067 15.8187 3.33025 15.8187 3.56511C15.8187 3.88005 15.9438 4.18209 16.1665 4.40479C16.3892 4.62748 16.6912 4.75259 17.0061 4.75259ZM19.8422 10.2941C19.633 10.2769 19.4256 10.3434 19.2655 10.479C19.1053 10.6147 19.0056 10.8084 18.9882 11.0175C18.8465 12.6725 18.2351 14.2526 17.2259 15.5719C16.2167 16.8912 14.8516 17.8948 13.2914 18.4646C11.7312 19.0345 10.0406 19.1468 8.41871 18.7884C6.79679 18.43 5.31097 17.6159 4.13607 16.4417C2.96117 15.2675 2.14607 13.7822 1.78668 12.1605C1.42729 10.5388 1.53855 8.84816 2.10738 7.28754C2.67621 5.72692 3.67895 4.36124 4.9976 3.35118C6.31625 2.34112 7.89596 1.7287 9.55085 1.58599C9.65481 1.57741 9.75606 1.54844 9.84883 1.50073C9.94159 1.45303 10.0241 1.38751 10.0915 1.30794C10.1589 1.22836 10.2101 1.13628 10.2419 1.03695C10.2738 0.937621 10.2858 0.832989 10.2772 0.729028C10.2686 0.625067 10.2396 0.523814 10.1919 0.431048C10.1442 0.338283 10.0787 0.255823 9.99914 0.188377C9.91956 0.12093 9.82748 0.069817 9.72815 0.0379568C9.62882 0.00609656 9.52419 -0.0058872 9.42023 0.00268958C7.4649 0.170881 5.59828 0.894031 4.03998 2.08706C2.48168 3.28009 1.29652 4.89337 0.623939 6.73709C-0.0486393 8.58081 -0.180658 10.5783 0.243415 12.4945C0.667488 14.4107 1.63001 16.1659 3.01774 17.5537C4.40547 18.9414 6.16068 19.9039 8.07687 20.328C9.99305 20.7521 11.9905 20.6201 13.8342 19.9475C15.6779 19.2749 17.2912 18.0897 18.4842 16.5314C19.6772 14.9731 20.4003 13.1064 20.5685 11.1511C20.5775 11.0471 20.5657 10.9423 20.534 10.8428C20.5023 10.7433 20.4512 10.6511 20.3837 10.5715C20.3162 10.4918 20.2336 10.4263 20.1406 10.3787C20.0477 10.3311 19.9463 10.3024 19.8422 10.2941ZM10.2772 3.16929C11.6863 3.16929 13.0638 3.58715 14.2355 4.37004C15.4072 5.15293 16.3204 6.26568 16.8596 7.56757C17.3989 8.86947 17.54 10.302 17.2651 11.6841C16.9901 13.0662 16.3116 14.3357 15.3152 15.3322C14.3187 16.3286 13.0492 17.0072 11.6672 17.2821C10.2851 17.557 8.85253 17.4159 7.55065 16.8766C6.24877 16.3374 5.13603 15.4242 4.35315 14.2525C3.57027 13.0808 3.15241 11.7033 3.15241 10.2941C3.15451 8.40515 3.90582 6.59414 5.24152 5.25842C6.57722 3.92271 8.38822 3.17138 10.2772 3.16929ZM9.48554 10.2941C9.48554 10.5041 9.56895 10.7055 9.71741 10.8539C9.86587 11.0024 10.0672 11.0858 10.2772 11.0858H15.027C15.237 11.0858 15.4383 11.0024 15.5868 10.8539C15.7353 10.7055 15.8187 10.5041 15.8187 10.2941C15.8187 10.0842 15.7353 9.88282 15.5868 9.73435C15.4383 9.58589 15.237 9.50249 15.027 9.50249H11.0688V5.54424C11.0688 5.33428 10.9854 5.13292 10.837 4.98446C10.6885 4.83599 10.4871 4.75259 10.2772 4.75259C10.0672 4.75259 9.86587 4.83599 9.71741 4.98446C9.56895 5.13292 9.48554 5.33428 9.48554 5.54424V10.2941ZM13.4437 2.37764C13.6786 2.37764 13.9082 2.30799 14.1035 2.17751C14.2987 2.04703 14.4509 1.86157 14.5408 1.64459C14.6307 1.42761 14.6542 1.18885 14.6084 0.958499C14.5626 0.728152 14.4495 0.516564 14.2834 0.350493C14.1173 0.184422 13.9058 0.0713256 13.6754 0.0255067C13.4451 -0.0203123 13.2063 0.00320364 12.9893 0.0930808C12.7723 0.182958 12.5869 0.335159 12.4564 0.530439C12.3259 0.725718 12.2563 0.955304 12.2563 1.19016C12.2563 1.5051 12.3814 1.80714 12.6041 2.02984C12.8268 2.25253 13.1288 2.37764 13.4437 2.37764Z"
      fill="#0E1F27"
    />
  </svg>
);

const IconClock23: React.FC = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M20.884 14.5V19.481L25.0391 21.964C25.2484 22.0892 25.3992 22.2921 25.4583 22.528C25.5174 22.764 25.48 23.0136 25.3543 23.2221C25.2286 23.4306 25.0249 23.5808 24.7881 23.6396C24.5512 23.6985 24.3005 23.6613 24.0912 23.5361L19.4898 20.7861C19.3536 20.7046 19.2409 20.5893 19.1627 20.4516C19.0845 20.3138 19.0434 20.1583 19.0434 20V14.5C19.0434 14.2569 19.1404 14.0238 19.313 13.8518C19.4856 13.6799 19.7197 13.5834 19.9637 13.5834C20.2078 13.5834 20.4419 13.6799 20.6145 13.8518C20.7871 14.0238 20.884 14.2569 20.884 14.5ZM19.9637 9.00003C18.512 8.99643 17.0739 9.27966 15.7328 9.83331C14.3916 10.387 13.1741 11.2001 12.1505 12.2256C11.3142 13.0689 10.5711 13.8801 9.84057 14.7292V12.6667C9.84057 12.4236 9.74362 12.1904 9.57103 12.0185C9.39844 11.8466 9.16436 11.75 8.92029 11.75C8.67621 11.75 8.44213 11.8466 8.26955 12.0185C8.09696 12.1904 8 12.4236 8 12.6667V17.25C8 17.4931 8.09696 17.7263 8.26955 17.8982C8.44213 18.0701 8.67621 18.1667 8.92029 18.1667H13.5217C13.7658 18.1667 13.9999 18.0701 14.1725 17.8982C14.3451 17.7263 14.442 17.4931 14.442 17.25C14.442 17.0069 14.3451 16.7738 14.1725 16.6018C13.9999 16.4299 13.7658 16.3334 13.5217 16.3334H10.8759C11.6984 15.3686 12.5175 14.4599 13.4516 13.518C14.7306 12.2441 16.3582 11.3737 18.1309 11.0159C19.9037 10.658 21.7431 10.8284 23.4192 11.5058C25.0953 12.1833 26.5338 13.3377 27.5549 14.8248C28.5761 16.3119 29.1347 18.0658 29.1609 19.8673C29.187 21.6688 28.6796 23.438 27.7021 24.9539C26.7245 26.4699 25.3201 27.6652 23.6644 28.3906C22.0087 29.1161 20.175 29.3394 18.3926 29.0328C16.6102 28.7261 14.958 27.9031 13.6425 26.6665C13.5546 26.5837 13.4512 26.519 13.3381 26.4761C13.2251 26.4331 13.1047 26.4128 12.9838 26.4162C12.8629 26.4196 12.7438 26.4467 12.6334 26.4959C12.523 26.5451 12.4234 26.6156 12.3403 26.7031C12.2572 26.7907 12.1923 26.8937 12.1491 27.0063C12.106 27.1189 12.0856 27.2388 12.089 27.3593C12.0924 27.4797 12.1196 27.5983 12.1691 27.7083C12.2185 27.8183 12.2892 27.9175 12.3771 28.0002C13.6879 29.2323 15.2815 30.1261 17.0188 30.6037C18.7562 31.0812 20.5844 31.128 22.3441 30.7399C24.1037 30.3517 25.7413 29.5406 27.1138 28.3771C28.4863 27.2137 29.5521 25.7333 30.2182 24.0652C30.8842 22.3971 31.1303 20.592 30.9349 18.8074C30.7395 17.0228 30.1086 15.313 29.0972 13.8271C28.0858 12.3413 26.7247 11.1245 25.1326 10.2831C23.5406 9.44171 21.766 9.0012 19.9637 9.00003Z"
      fill="#0E1F27"
    />
  </svg>
);

const DateNavigationHeader: React.FC<DateNavigationHeaderProps> = ({
  onDateChange,
  disabled = false,
  hasTasks,
  jwtToken,
  totalTaskHours = 0,
  userInitials = 'AP',
}) => {
  const [currentDate, setCurrentDate] = useState<moment.Moment>(() => moment());

  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= MOBILE_BREAKPOINT;
  });

  const [desktopWeekPickerOpen, setDesktopWeekPickerOpen] = useState(false);
  const [mobileWeekExpanded, setMobileWeekExpanded] = useState(false);
  const [taskHoursByDate, setTaskHoursByDate] = useState<Record<string, number>>({});

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Only call onDateChange on initial mount
  useEffect(() => {
    onDateChange(currentDate.format('YYYY-MM-DD'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep week strips anchored to today, not currently selected date.
  const selectedWeekStart = useMemo(() => moment().startOf('isoWeek'), []);
  const selectedWeekDaysMobile = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) => selectedWeekStart.clone().add(i, 'day'));
  }, [selectedWeekStart]);
  const selectedWeekDaysPc = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) => selectedWeekStart.clone().add(i, 'day'));
  }, [selectedWeekStart]);

  const previousWeekStart = useMemo(() => selectedWeekStart.clone().subtract(1, 'week'), [selectedWeekStart]);
  const previousWeekDaysMobile = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) => previousWeekStart.clone().add(i, 'day'));
  }, [previousWeekStart]);
  const previousWeekDaysPc = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) => previousWeekStart.clone().add(i, 'day'));
  }, [previousWeekStart]);

  useEffect(() => {
    const load = async () => {
      if (!jwtToken) return;
      const start = previousWeekStart.format('YYYY-MM-DD');
      const end = selectedWeekStart.clone().add(6, 'day').format('YYYY-MM-DD');
      try {
        const res = await fetchDailyTaskCountByDateRange(jwtToken, { start, end });
        if (res?.status === '1' && res?.response && typeof res.response === 'object') {
          setTaskHoursByDate(res.response);
        }
      } catch (e) {
        console.error('Failed to load daily task counts:', e);
      }
    };
    load();
  }, [jwtToken, previousWeekStart, selectedWeekStart, currentDate]);

  const handleDateChangeRequest = async (newDate: moment.Moment) => {
    if (disabled) return;

    // Close expanded UI immediately; selection will still happen after confirmation
    setDesktopWeekPickerOpen(false);
    setMobileWeekExpanded(false);

    const changeDate = () => {
      setCurrentDate(newDate);
      onDateChange(newDate.format('YYYY-MM-DD'));
    };

    changeDate();
  };

  const dateLabel = useMemo(() => {
    const isToday = currentDate.isSame(moment(), 'day');
    if (isToday) return `Today, ${currentDate.format('ddd')}`;
    return `${currentDate.format('MMM-DD')}, ${currentDate.format('ddd')}`;
  }, [currentDate]);

  const totalTaskHoursLabel = useMemo(() => {
    const safeHours = Number.isFinite(totalTaskHours) ? Math.max(totalTaskHours, 0) : 0;
    const h = Math.floor(safeHours);
    const m = Math.round((safeHours - h) * 60);
    if (m === 0) return `${h}h`;
    return `${h}h ${m} min`;
  }, [totalTaskHours]);

  const WeekPickerContent = (
    <div
      style={{
        maxWidth: 900,
        width: '100%',
        background: 'linear-gradient(180deg, #FFF 0%, rgba(251, 251, 247, 0.60) 100%)',
        borderRadius: '0 0 32px 32px',
        boxShadow: '0 8px 30px 0 rgba(0, 0, 0, 0.08)',
        backdropFilter: 'blur(10px)',
        paddingBottom: 18,
      }}
    >
      <div
        style={{
          padding: '14px 20px 10px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: '#171717',
          fontSize: 14,
          fontWeight: 600,
          borderBottom: '1px solid rgba(0,0,0,0.04)',
        }}
      >
        <span>Select an earlier date</span>
        <button
          type="button"
          aria-label="Close"
          onClick={() => setDesktopWeekPickerOpen(false)}
          style={{
            width: 28,
            height: 28,
            borderRadius: 9999,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: '#171717',
            fontSize: 18,
            lineHeight: '28px',
            padding: 0,
          }}
        >
          ×
        </button>
      </div>

      <div style={{ padding: '14px 16px 0' }}>
        <WeekStrip
          days={previousWeekDaysPc}
          selected={currentDate}
          disabled={disabled}
          stopOpenPropagation={false}
          onSelectDay={(day) => handleDateChangeRequest(day)}
          variant="pc"
          taskHoursByDate={taskHoursByDate}
        />
        <div style={{ height: 10 }} />
        <WeekStrip
          days={selectedWeekDaysPc}
          selected={currentDate}
          disabled={disabled}
          stopOpenPropagation={false}
          onSelectDay={(day) => handleDateChangeRequest(day)}
          variant="pc"
          taskHoursByDate={taskHoursByDate}
        />
      </div>
    </div>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: isMobile ? 'flex-start' : 'space-between',
        padding: isMobile ? '16px' : '18px',
        background: isMobile
          ? 'linear-gradient(180deg, #FFF 0%, #FFF 100%)'
          : 'linear-gradient(180deg, #FFF 0%, rgba(251, 251, 247, 0.60) 100%)',
        position: 'relative',
        borderRadius: isMobile ? '0 0 30px 30px' : '0 0 32px 32px',
        boxShadow: '0 8px 30px 0 rgba(0, 0, 0, 0.08)',
        backdropFilter: 'blur(10px)',
        width: '100%',
        maxWidth: isMobile ? 'none' : 900,
        margin: isMobile ? 0 : '0 auto',
        gap: isMobile && mobileWeekExpanded ? 12 : 0,
      }}
    >
      {!isMobile ? (
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '0 12px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 9999,
                  background: '#F0F2F5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#6b7280',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {userInitials}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 18, minWidth: 0 }}>
                <div
                  style={{
                    color: '#171717',
                    fontSize: 24,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {dateLabel}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0E1F27', fontSize: 16,fontWeight:500 }}>
                  <IconClock21 />
                  <span style={{ whiteSpace: 'nowrap' }}>{totalTaskHoursLabel}</span>
                </div>
              </div>

          </div>

          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 14 }}>
            <Popover
              open={desktopWeekPickerOpen}
              onOpenChange={(open) => setDesktopWeekPickerOpen(open)}
              placement="bottom"
              trigger="click"
              content={WeekPickerContent}
              arrow={false}
              overlayStyle={{ padding: 0 }}
              overlayInnerStyle={{ padding: 0 }}
            >
              <button
                type="button"
                disabled={disabled}
                aria-label="Open week picker"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <IconClock23 />
              </button>
            </Popover>

            <WeekStrip
              days={selectedWeekDaysPc}
              selected={currentDate}
              disabled={disabled}
              stopOpenPropagation
              onSelectDay={(day) => handleDateChangeRequest(day)}
              variant="pc"
              taskHoursByDate={taskHoursByDate}
            />
          </div>
        </div>
      ) : (
        <div style={{ width: '100%' }}>
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 9999,
                  background: '#F0F2F5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#6b7280',
                  fontSize: 12,
                  fontWeight: 600,
                  flex: '0 0 auto',
                }}
              >
                {userInitials}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
                <div
                  style={{
                    color: '#171717',
                    fontSize: 18,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {dateLabel}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0E1F27', fontSize: 14, fontWeight: 500 }}>
                  <IconClock21 />
                  <span style={{ whiteSpace: 'nowrap' }}>{totalTaskHoursLabel}</span>
                </div>
              </div>
            </div>

            <button
              type="button"
              disabled={disabled}
              aria-label="Toggle week calendar"
              onClick={() => {
                if (disabled) return;
                setMobileWeekExpanded((v) => !v);
              }}
              style={{
                width: 40,
                height: 40,
                borderRadius: 9999,
                border: 'none',
                background: '#0E1F27',
                color: '#ffffff',
                fontSize: 12,
                fontWeight: 600,
                cursor: disabled ? 'not-allowed' : 'pointer',
                flex: '0 0 auto',
              }}
            >
              {currentDate.date()}
            </button>
          </div>

          {mobileWeekExpanded && (
            <div style={{ paddingTop: 8, paddingBottom: 2 }}>
              <WeekStrip
                days={previousWeekDaysMobile}
                selected={currentDate}
                disabled={disabled}
                stopOpenPropagation={false}
                onSelectDay={(day) => handleDateChangeRequest(day)}
                variant="pc"
                taskHoursByDate={taskHoursByDate}
              />
              <div style={{ height: 12 }} />
              <WeekStrip
                days={selectedWeekDaysMobile}
                selected={currentDate}
                disabled={disabled}
                stopOpenPropagation={false}
                onSelectDay={(day) => handleDateChangeRequest(day)}
                variant="pc"
                taskHoursByDate={taskHoursByDate}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DateNavigationHeader;
